import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabase";
import {
  enqueueAvatarPipeline,
  getAvatarSession,
  type AvatarPipelineStepRow,
  type AvatarSessionDetail,
} from "../api";

function formatSeconds(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v} s`;
}

function stepStatusLabel(status: string): string {
  switch (status) {
    case "pending":
      return "En attente";
    case "running":
      return "En cours";
    case "done":
      return "Terminé";
    case "failed":
      return "Échec";
    default:
      return status;
  }
}

export default function AvatarSessionShare() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const nav = useNavigate();
  const [session, setSession] = useState<AvatarSessionDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav("/admin/login", { replace: true });
    });
  }, [nav]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const s = await getAvatarSession(sessionId);
        if (!cancelled) {
          setSession(s);
          const fast = s.status === "queued" || s.status === "processing";
          timeout = setTimeout(poll, fast ? 2000 : 8000);
        }
      } catch (e: unknown) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Erreur");
        if (!cancelled) timeout = setTimeout(poll, 8000);
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [sessionId]);

  const runEnqueue = async () => {
    if (!sessionId) return;
    setBusy(true);
    setErr(null);
    try {
      const s = await enqueueAvatarPipeline(sessionId);
      setSession(s);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Enqueue échoué");
    } finally {
      setBusy(false);
    }
  };

  if (!sessionId) return null;

  return (
    <div className="min-h-screen bg-ivory p-6 max-w-lg mx-auto">
      <h1 className="text-xl font-semibold text-charcoal">Session avatar</h1>
      <p className="text-sm text-concrete mt-1 font-mono break-all">{sessionId}</p>

      {err && <p className="mt-4 text-sm text-red-700">{err}</p>}

      {session && (
        <div className="mt-6 space-y-4 text-sm">
          <div>
            <div className="text-concrete text-xs font-mono uppercase">Langues</div>
            <div className="font-medium font-mono text-sm">
              {(session.langs && session.langs.length ? session.langs : [session.lang]).join(", ")}
            </div>
          </div>

          <div>
            <div className="text-concrete text-xs font-mono uppercase">Statut</div>
            <div className="font-medium">{session.status}</div>
            {session.pipeline_step && (
              <div className="text-concrete text-xs mt-1">Étape : {session.pipeline_step}</div>
            )}
            {session.pipeline_error && (
              <div className="text-red-700 text-xs mt-2 whitespace-pre-wrap">
                {session.pipeline_error}
              </div>
            )}
          </div>

          {(session.pipeline_timeline ?? []).length > 0 && (
            <div>
              <div className="text-concrete text-xs font-mono uppercase mb-2">
                Étapes (Kling, TTS, lip-sync)
              </div>
              <div className="border border-stone rounded-lg overflow-hidden text-xs">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-stone/30 text-concrete text-left">
                      <th className="font-normal p-2 border-b border-stone">Étape</th>
                      <th className="font-normal p-2 border-b border-stone w-24">Statut</th>
                      <th className="font-normal p-2 border-b border-stone w-20 text-right">
                        Temps
                      </th>
                      <th className="font-normal p-2 border-b border-stone w-24 text-right">
                        Typ.
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(session.pipeline_timeline ?? []).map((row: AvatarPipelineStepRow) => (
                      <tr key={row.step} className="border-b border-stone last:border-b-0">
                        <td className="p-2 text-charcoal align-top">{row.label}</td>
                        <td className="p-2 text-concrete align-top">
                          {stepStatusLabel(row.status)}
                        </td>
                        <td className="p-2 text-right font-mono align-top text-concrete">
                          {row.status === "running"
                            ? `${formatSeconds(row.elapsed_sec)} écoulé`
                            : formatSeconds(row.duration_sec)}
                        </td>
                        <td className="p-2 text-right font-mono align-top text-concrete">
                          ~
                          {row.eta_hint_sec != null
                            ? `${Math.round(row.eta_hint_sec)} s`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-concrete text-[10px] mt-1 leading-snug">
                « Typ. » = ordre de grandeur pour l’interface (pas un engagement de durée). « Temps »
                = durée réelle une fois l’étape terminée, ou temps écoulé pendant l’étape en cours.
              </p>
            </div>
          )}

          <div>
            <div className="text-concrete text-xs font-mono uppercase mb-1">Lien d’enregistrement</div>
            <a
              href={session.recording_url}
              target="_blank"
              rel="noreferrer"
              className="text-charcoal underline break-all"
            >
              {session.recording_url}
            </a>
          </div>

          {session.status === "ready_to_enqueue" && (
            <p className="text-concrete text-xs bg-ivory-tint border border-stone rounded-lg px-3 py-2">
              Une vidéo déjà prête sert de source de mouvement. Tu peux lancer la génération tout
              de suite. Pour un autre enregistrement, crée une nouvelle session depuis le créateur
              d&apos;avatar.
            </p>
          )}

          {session.status === "awaiting_recording" && (
            <p className="text-concrete text-xs">
              Enregistre-toi sur ce lien (même onglet ou téléphone). Quand la vidéo apparaît dans
              l’admin Videolink comme prête, clique le bouton ci-dessous pour lancer Kling → TTS →
              lip-sync.
            </p>
          )}

          <button
            type="button"
            disabled={
              busy ||
              session.status === "queued" ||
              session.status === "processing" ||
              session.status === "done"
            }
            onClick={runEnqueue}
            className="w-full py-3 bg-charcoal text-ivory rounded-lg font-mono text-xs uppercase disabled:opacity-50"
          >
            {busy ? "…" : "Lancer la génération avatar"}
          </button>

          {session.final_download_urls &&
            Object.keys(session.final_download_urls).length > 0 && (
              <div className="space-y-2">
                <div className="text-concrete text-xs font-mono uppercase">
                  Vidéos finales (par langue)
                </div>
                {Object.entries(session.final_download_urls).map(([lng, url]) => (
                  <a
                    key={lng}
                    href={url}
                    className="block w-full py-3 text-center border border-charcoal rounded-lg font-mono text-xs uppercase"
                  >
                    Télécharger · {lng.toUpperCase()}
                  </a>
                ))}
              </div>
            )}

          {session.final_download_url &&
            (!session.final_download_urls ||
              Object.keys(session.final_download_urls).length === 0) && (
              <a
                href={session.final_download_url}
                className="block w-full py-3 text-center border border-charcoal rounded-lg font-mono text-xs uppercase"
              >
                Télécharger la vidéo finale
              </a>
            )}
        </div>
      )}
    </div>
  );
}
